import { Injectable, inject } from '@angular/core';

import { Logger } from '@frontend/vanilla/core';

import { ModalGestureDebounceUtil } from './modal-gesture-debounce.util';
import { ModalGestureHelperService } from './modal-gesture-helper.service';
import { ModalGestureQueriesService } from './modal-gesture-queries.service';
import { Background } from './modal-gesture.model';

const LOG_TAG = 'Gestures';

@Injectable({ providedIn: 'root' })
export class ModalGestureBackgroundService {
    readonly #logger = inject(Logger);
    readonly #modalGestureQueries = inject(ModalGestureQueriesService);
    readonly #modalGestureHelper = inject(ModalGestureHelperService);

    readonly #debounceUtil = new ModalGestureDebounceUtil();

    #cachedBackground?: Background;

    getBackground(): Background {
        if (!this.#debounceUtil.isDebounced()) {
            return this.useCachedBackground();
        }

        return this.updateBackground();
    }

    private updateBackground(): Background {
        return (this.#cachedBackground = this.getCurrentBackground());
    }

    private useCachedBackground(): Background {
        return this.#cachedBackground ?? this.getCurrentBackground();
    }

    private getCurrentBackground(): Background {
        const activeModalWindowsExcludingCurrent = this.#modalGestureQueries.getActiveModalWindowsExcludingCurrent();

        if (activeModalWindowsExcludingCurrent.length === 0) {
            return this.getBackgroundIsDefault();
        }

        return this.getBackgroundIsModal(activeModalWindowsExcludingCurrent);
    }

    private getBackgroundIsDefault(): Background {
        const header = this.#modalGestureQueries.getBackgroundSlotHeader();
        const main = this.#modalGestureQueries.getBackgroundMainColumn();

        if (!header || !main) {
            this.#logger.error(`[${LOG_TAG}] attempted to get the default background elements but one or more are null`);

            return { type: 'default', elements: [] };
        }

        return { type: 'default', elements: [header, main] };
    }

    private getBackgroundIsModal(activeModals: HTMLElement[]): Background {
        const backgroundModal = activeModals[activeModals.length - 1];

        if (this.#modalGestureHelper.isEventDetailsModal(backgroundModal)) {
            return { type: 'event', elements: [backgroundModal] };
        }

        return { type: 'sheet', elements: [backgroundModal] };
    }
}
